import { getChartOptions, getTooltipTableRow, getTooltipWrapper } from '@/helpers/charts/chartSettings.ts';
import dayjs from 'dayjs';
import {
    Options, SeriesOptionsType, TooltipFormatterContextObject, XAxisOptions,
    YAxisOptions,
} from 'highcharts';
import { IFlexIndexChart, IFlexIndexSeries } from '@/interfaces/benchmarks/flexIndex/IFlexIndex.ts';
import { IFlexIndexGroupOptions } from '@/interfaces/benchmarks/flexIndex/IFlexIndexGroup.ts';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IDateRangeByString } from '@/interfaces/general/IDateRangeByString.ts';
import { DEFAULT_FLEX_INDEX_GROUP_OPTIONS } from '@/constants/benchmarks/flexIndexConstants.ts';
import { getDateRangeFromDayOnlyDatePicker } from '@/utils/dateUtils.ts';
import { toSpacedPascalCase } from '@/utils/stringUtils.ts';

type LegacyFlexIndexGroupOptions = IFlexIndexGroupOptions & { selectedDateRange?: IDateRangeByString };

/** When all negative values are at most this fraction of the chart value range, they are treated as noise and clamped to 0. */
const DRILLDOWN_NEGATIVE_CLAMP_MAX_MAGNITUDE_RATIO = 0.1;

function toFiniteChartNumber (value: unknown): number | undefined {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : undefined;
}

function clampNegativeSeriesValuesToZero (values: ReadonlyArray<number>): Array<number> {
    return values.map((value) => {
        const n = toFiniteChartNumber(value);
        if (n === undefined) return typeof value === 'number' ? value : Number(value);
        return n < 0 ? 0 : n;
    });
}

function getDrilldownSlicedSeriesData (series: IFlexIndexSeries, viewport: IFlexIndexTruncationViewport): Array<number> {
    return viewport.shouldUseFullSeries
        ? series.data
        : series.data.slice(viewport.startIndex, viewport.endIndex);
}

function computeDrilldownClampValueRange (
    allSlicedSeries: ReadonlyArray<ReadonlyArray<number>>,
    chartType: string,
): number {
    const isStackedColumn = chartType.toLowerCase() === 'column';

    if (isStackedColumn && allSlicedSeries.length > 0) {
        const maxLen = Math.max(0, ...allSlicedSeries.map(s => s.length));
        if (maxLen === 0) return 0;

        const perIndexTotals = Array.from({ length: maxLen }, (_, index) => allSlicedSeries.reduce(
            (sum, series) => sum + (toFiniteChartNumber(series[index]) ?? 0),
            0,
        ));
        const finiteTotals = perIndexTotals.filter(t => Number.isFinite(t));
        if (finiteTotals.length === 0) return 0;
        return Math.max(...finiteTotals) - Math.min(...finiteTotals);
    }

    const finiteValues = allSlicedSeries.flatMap(data => data.map(toFiniteChartNumber).filter((n): n is number => n !== undefined));
    if (finiteValues.length === 0) return 0;
    return Math.max(...finiteValues) - Math.min(...finiteValues);
}

function shouldApplySmallNegativeClampForDrilldown (
    allSlicedSeries: ReadonlyArray<ReadonlyArray<number>>,
    chartType: string,
): boolean {
    const chartValueRange = computeDrilldownClampValueRange(allSlicedSeries, chartType);
    const maxNegativeMagnitudeToClamp = DRILLDOWN_NEGATIVE_CLAMP_MAX_MAGNITUDE_RATIO * chartValueRange;

    const hasSignificantNegative = allSlicedSeries.some(data => data.some((raw) => {
        const v = toFiniteChartNumber(raw);
        return v !== undefined && v < 0 && Math.abs(v) > maxNegativeMagnitudeToClamp;
    }));

    return !hasSignificantNegative;
}

function getDateModelFromFlexIndexOptions (options: LegacyFlexIndexGroupOptions): IAdvancedDatePickerModel {
    if (options.selectedDateModel) return options.selectedDateModel;
    if (options.selectedDateRange) return { dateType: 'fixed', dateRange: options.selectedDateRange };
    return DEFAULT_FLEX_INDEX_GROUP_OPTIONS.selectedDateModel;
}

function resolveFlexIndexDateRangeByString (options: LegacyFlexIndexGroupOptions): IDateRangeByString {
    const range = getDateRangeFromDayOnlyDatePicker(getDateModelFromFlexIndexOptions(options));
    return { from: range.from.toISOString(), to: range.to.toISOString() };
}

function getXAxisDateLabelFormat (granularity: string): string {
    switch (granularity.toLowerCase()) {
        case 'month':
            return '{value:%b %Y}';
        case 'year':
            return '{value:%Y}';
        case 'day':
        default:
            return '{value:%e %b %Y}';
    }
}

function formatUnit (unit: string | null | undefined): string | null {
    if (!unit) return null;
    switch (unit) {
        case 'Pound':
            return '£';
        case 'PoundPerKwPerYear':
            return '£/kW/year';
        case 'PoundPerMw':
            return '£/MW';
        case 'PoundPerMwh':
            return '£/MWh';
        default:
            return unit;
    }
}

function tooltipFormatter (this: TooltipFormatterContextObject): string {
    let rows: string = '';
    this.points?.forEach((point: TooltipFormatterContextObject) => {
        const y = ((point?.y || 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
        const suffix = formatUnit((point.series.userOptions as SeriesOptionsType & { unit?: string }).unit ?? null);
        rows += getTooltipTableRow({
            label: point.point.series.name,
            value: `${y}${suffix ? ` (${suffix})` : ''}`,
            color: point.color,
        });
    });
    return getTooltipWrapper({
        content: rows,
        contentType: 'table',
        header: `${dayjs(this.x).format('D MMM YYYY')}`,
    });
}

function generateDateRangeArray (dateRange: IDateRangeByString): Array<string> {
    const start = dayjs(dateRange.from);
    const end = dayjs(dateRange.to);

    const dates: Array<string> = [];
    let currentDate = start;
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
        dates.push(currentDate.toISOString());
        currentDate = currentDate.add(1, 'day');
    }

    return dates;
}

function getTruncationParams (
    chartStart: string,
    chartEnd: string,
    optionsStart: dayjs.Dayjs,
    optionsEnd: dayjs.Dayjs,
    granularity: string,
): { startIndex: number; endIndex: number; pointStart: number } {
    const chartStartDay = dayjs(chartStart);
    const chartEndDay = dayjs(chartEnd);
    const intervalUnit = granularity.toLowerCase() as 'day' | 'month' | 'year';

    const optStart = optionsStart.startOf('day');
    const optEnd = optionsEnd.startOf('day');
    const rangeStart = chartStartDay.startOf('day');
    const rangeEnd = chartEndDay.startOf('day');

    if (optEnd.isBefore(rangeStart, 'day') || optStart.isAfter(rangeEnd, 'day')) {
        return {
            startIndex: 0,
            endIndex: 0,
            pointStart: rangeStart.valueOf(),
        };
    }

    if (!(optionsStart.isAfter(chartStartDay) || optionsEnd.isBefore(chartEndDay))) {
        const fullRangeCount = chartEndDay.diff(chartStartDay, intervalUnit) + 1;
        return {
            startIndex: 0,
            endIndex: fullRangeCount,
            pointStart: Date.parse(chartStart),
        };
    }

    const effectiveStart = optionsStart.isAfter(chartStartDay) ? optionsStart : chartStartDay;
    const effectiveEnd = optionsEnd.isBefore(chartEndDay) ? optionsEnd : chartEndDay;

    const startIndex = effectiveStart.diff(chartStartDay, intervalUnit);
    const endIndex = effectiveEnd.diff(chartStartDay, intervalUnit) + 1;

    if (startIndex < 0 || endIndex <= startIndex) {
        return {
            startIndex: 0,
            endIndex: 0,
            pointStart: rangeStart.valueOf(),
        };
    }

    return {
        startIndex,
        endIndex,
        pointStart: effectiveStart.valueOf(),
    };
}

interface IFlexIndexTruncationViewport {
    startIndex: number;
    endIndex: number;
    pointStart: number;
    shouldUseFullSeries: boolean;
}

function getFlexIndexTruncationViewport (
    chartStart: string,
    chartEnd: string,
    optionsStart: dayjs.Dayjs,
    optionsEnd: dayjs.Dayjs,
    granularity: string,
    referenceSeriesPointCount: number,
): IFlexIndexTruncationViewport {
    const raw = getTruncationParams(chartStart, chartEnd, optionsStart, optionsEnd, granularity);
    const shouldUseFullSeries = referenceSeriesPointCount === 0
        || (raw.startIndex === 0 && raw.endIndex >= referenceSeriesPointCount);
    return {
        startIndex: raw.startIndex,
        endIndex: raw.endIndex,
        pointStart: shouldUseFullSeries ? Date.parse(chartStart) : raw.pointStart,
        shouldUseFullSeries,
    };
}

function truncateChartDataToDateRange (
    chart: IFlexIndexChart,
    optionsStart: dayjs.Dayjs,
    optionsEnd: dayjs.Dayjs,
): { data: Array<number>; pointStart: number } {
    const originalData = chart.data[0].data;
    const viewport = getFlexIndexTruncationViewport(
        chart.start,
        chart.end,
        optionsStart,
        optionsEnd,
        chart.aggregationOption.granularity,
        originalData.length,
    );

    if (viewport.shouldUseFullSeries) {
        return {
            data: originalData,
            pointStart: viewport.pointStart,
        };
    }

    return {
        data: originalData.slice(viewport.startIndex, viewport.endIndex),
        pointStart: viewport.pointStart,
    };
}

function constructOverlayChartOptions (chartsData: Array<IFlexIndexChart & { name: string }>, dateRange: IDateRangeByString, darkMode: boolean): Options {
    const optionsStart = dayjs(dateRange.from);
    const optionsEnd = dayjs(dateRange.to);
    const granularity = chartsData.length !== 0 ? chartsData[0].aggregationOption.granularity.toLowerCase() : 'day';

    const unit = chartsData.length !== 0 ? formatUnit(chartsData[0].unit) : null;
    const defaultOptions = getChartOptions(darkMode, false);

    const baseXAxis: XAxisOptions = Array.isArray(defaultOptions.xAxis)
        ? (defaultOptions.xAxis[0] as XAxisOptions)
        : (defaultOptions.xAxis as XAxisOptions) || {} as XAxisOptions;

    const baseYAxis: YAxisOptions = Array.isArray(defaultOptions.yAxis)
        ? (defaultOptions.yAxis[0] as YAxisOptions)
        : (defaultOptions.yAxis as YAxisOptions) || {} as YAxisOptions;

    const options: Options = {
        ...defaultOptions,
        chart: {
            ...defaultOptions.chart,
            spacingTop: 10,
            spacingRight: 20,
            spacingBottom: 30,
            spacingLeft: 20,
        },
        legend: {
            ...defaultOptions.legend,
            y: 25,
        },
        xAxis: [{
            ...baseXAxis,
            type: 'datetime',
            labels: {
                ...(baseXAxis.labels ?? {}),
                format: getXAxisDateLabelFormat(granularity),
            },
        }],
        yAxis: [{
            ...baseYAxis,
            title: {
                ...(baseYAxis.title ?? {}),
                text: `Value of Flex ${unit ?? ''}`,
                x: -10,
            },
        }],
        series: chartsData.map(chart => {
            const truncated = truncateChartDataToDateRange(chart, optionsStart, optionsEnd);
            return {
                name: chart.name,
                data: truncated.data,
                type: 'line',
                pointStart: truncated.pointStart,
                pointIntervalUnit: granularity,
                unit: chart.unit ?? undefined,
            };
        }) as Options['series'],
        tooltip: {
            ...defaultOptions.tooltip,
            formatter: tooltipFormatter,
        },
    };

    return options;
}

function constructDrillDownChartOptions (
    chartData: IFlexIndexChart,
    dateRange: IDateRangeByString,
    darkMode: boolean,
    colour: string,
): Options {
    const unit = chartData.unit ? formatUnit(chartData.unit) : null;
    const defaultOptions = getChartOptions(darkMode, false);
    const chartType = chartData.chartType?.toLowerCase() ?? 'line';
    const granularity = chartData.aggregationOption.granularity.toLowerCase();

    const baseXAxis: XAxisOptions = Array.isArray(defaultOptions.xAxis)
        ? (defaultOptions.xAxis[0] as XAxisOptions)
        : (defaultOptions.xAxis as XAxisOptions) || {} as XAxisOptions;

    const baseYAxis: YAxisOptions = Array.isArray(defaultOptions.yAxis)
        ? (defaultOptions.yAxis[0] as YAxisOptions)
        : (defaultOptions.yAxis as YAxisOptions) || {} as YAxisOptions;

    const referenceLength = Math.max(0, ...chartData.data.map(x => x.data.length));
    const viewport = getFlexIndexTruncationViewport(
        chartData.start,
        chartData.end,
        dayjs(dateRange.from),
        dayjs(dateRange.to),
        chartData.aggregationOption.granularity,
        referenceLength,
    );

    const slicedPerSeries = chartData.data.map(series => getDrilldownSlicedSeriesData(series, viewport));
    const applySmallNegativeClamp = shouldApplySmallNegativeClampForDrilldown(slicedPerSeries, chartType);

    const options: Options = {
        ...defaultOptions,
        chart: {
            ...defaultOptions.chart,
            spacingTop: 10,
            spacingRight: 20,
            spacingBottom: 30,
            spacingLeft: 20,
            type: chartType,
        },
        legend: {
            ...defaultOptions.legend,
            y: 25,
        },
        plotOptions: {
            ...defaultOptions.plotOptions,
            ...(chartType === 'column' && {
                column: {
                    stacking: 'normal',
                },
            }),
        },
        xAxis: [{
            ...baseXAxis,
            type: 'datetime',
            labels: {
                ...(baseXAxis.labels ?? {}),
                format: getXAxisDateLabelFormat(granularity),
            },
        }],
        yAxis: [{
            ...baseYAxis,
            title: {
                ...(baseYAxis.title ?? {}),
                text: `${unit}`,
                x: -10,
            },
        }],
        series: chartData.data.map((x, seriesIndex) => {
            const sliced = slicedPerSeries[seriesIndex];
            if (sliced === undefined) {
                throw new Error('Drilldown series slice missing for index');
            }
            return {
                name: toSpacedPascalCase(x.name),
                data: applySmallNegativeClamp ? clampNegativeSeriesValuesToZero(sliced) : sliced,
                type: chartType as SeriesOptionsType['type'],
                pointStart: viewport.pointStart,
                pointIntervalUnit: granularity,
                color: chartData.data.length === 1 ? colour : undefined,
                unit,
            };
        }) as Options['series'],
        tooltip: {
            ...defaultOptions.tooltip,
            formatter: tooltipFormatter,
            outside: false,
        },
    };
    return options;
}

export {
    constructOverlayChartOptions,
    constructDrillDownChartOptions,
    formatUnit,
    generateDateRangeArray,
    getDateModelFromFlexIndexOptions,
    resolveFlexIndexDateRangeByString,
};
